import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Copy, CheckCircle2, Gift, Users, TrendingUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * ShareReferralCard
 * ─────────────────
 * Prominent card on the Driver (Supplier) Dashboard that displays the
 * driver's unique DR-XXXXXX referral code and lets them invite friends in
 * two taps via the native share sheet (`navigator.share`). Falls back to
 * clipboard copy when the Web Share API is not available (desktop browsers).
 *
 * Backend contract: GET /api/me/referral-code → { referral_code,
 * referred_count, total_earned: [{ currency, amount }, ...] }
 */
export default function ShareReferralCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get("/me/referral-code");
        if (mounted) setData(res.data);
      } catch (err) {
        // Silent fail — card just won't render if the API is down.
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const buildShareText = (code) =>
    t(
      "referralCard.shareText",
      "Join me on Drivons — the on-demand marketplace for taxis, moving, towing & more. Use my code {{code}} for a bonus on your first trip!",
      { code }
    );

  const shareUrl = () => {
    const code = data?.referral_code;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return code ? `${origin}/?ref=${encodeURIComponent(code)}` : origin;
  };

  const handleCopy = async () => {
    if (!data?.referral_code) return;
    try {
      await navigator.clipboard.writeText(data.referral_code);
      setCopied(true);
      toast.success(t("referralCard.copied", "Code copied to clipboard"));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t("referralCard.copyFailed", "Could not copy code"));
    }
  };

  const handleShare = async () => {
    if (!data?.referral_code) return;
    const code = data.referral_code;
    const text = buildShareText(code);
    const url = shareUrl();
    setSharing(true);
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({
          title: t("referralCard.shareTitle", "Invite friends to Drivons"),
          text,
          url,
        });
      } else {
        // Fallback for desktop browsers without Web Share API.
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success(
          t("referralCard.linkCopied", "Invite link copied — paste it anywhere")
        );
      }
    } catch (err) {
      // AbortError = user cancelled the share sheet — swallow silently.
      if (err?.name !== "AbortError") {
        toast.error(formatApiError(err) || t("referralCard.shareFailed", "Could not open share sheet"));
      }
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div
        className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] p-6 flex items-center justify-center min-h-[120px]"
        data-testid="share-referral-card-loading"
      >
        <Loader2 className="animate-spin text-white/60" size={20} />
      </div>
    );
  }

  if (!data?.referral_code) return null;

  const earnedLine = (data.total_earned || [])
    .map((e) => `${e.amount.toFixed(0)} ${e.currency.toUpperCase()}`)
    .join(" · ");

  return (
    <div
      className="mt-4 rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]"
      data-testid="share-referral-card"
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center">
            <Gift size={18} strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-white/50">
              {t("referralCard.eyebrow", "Refer & earn")}
            </div>
            <div className="text-[15px] font-bold text-white mt-0.5">
              {t("referralCard.title", "Share your code")}
            </div>
          </div>
        </div>

        <p className="mt-3 text-[12px] text-white/60 leading-relaxed">
          {t(
            "referralCard.subtitle",
            "Give friends a bonus on their first ride — earn a reward when they take it."
          )}
        </p>

        {/* Big DR-XXXXXX display */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-5">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-white/40">
            {t("referralCard.yourCode", "Your code")}
          </div>
          <div
            className="mt-1 font-black text-white tabular-nums tracking-[0.15em] text-3xl sm:text-4xl select-all break-all"
            data-testid="share-referral-code"
          >
            {data.referral_code}
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={handleCopy}
            className="rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/10 transition-colors text-white px-4 py-2.5 text-[12px] font-bold inline-flex items-center justify-center gap-2"
            data-testid="share-referral-copy-btn"
          >
            {copied ? (
              <>
                <CheckCircle2 size={14} className="text-brand-accent" />
                {t("referralCard.copiedShort", "Copied")}
              </>
            ) : (
              <>
                <Copy size={14} />
                {t("referralCard.copy", "Copy code")}
              </>
            )}
          </button>
          <button
            onClick={handleShare}
            disabled={sharing}
            className="rounded-full bg-white hover:bg-white/90 transition-colors text-black px-4 py-2.5 text-[12px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="share-referral-share-btn"
          >
            {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} strokeWidth={2.5} />}
            {t("referralCard.share", "Share")}
          </button>
        </div>

        {/* Stats footer */}
        <div className="mt-4 flex items-center gap-4 text-[11px] text-white/60 flex-wrap">
          <span className="inline-flex items-center gap-1.5" data-testid="share-referral-count">
            <Users size={12} />
            <span className="font-bold text-white tabular-nums">{data.referred_count || 0}</span>
            {t("referralCard.invited", "invited")}
          </span>
          {earnedLine && (
            <span className="inline-flex items-center gap-1.5" data-testid="share-referral-earned">
              <TrendingUp size={12} className="text-brand-accent" />
              <span className="font-bold text-brand-accent tabular-nums">{earnedLine}</span>
              {t("referralCard.earnedSuffix", "earned")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
